import { Alert, Box, Chip, Stack } from "@mui/material";
import pluralize from "pluralize";

import { MergeImpact } from "./inventoryOverlaps";

export type MergeOutcome = {
  autoMergeCount: number;
  keptCount: number;
  combinedCount: number;
  blankCount: number;
};

type MergeImpactSummaryProps = {
  impact: MergeImpact;
  mode: "preview" | "outcome";
  outcome?: MergeOutcome;
};

function PreviewSummary({ impact }: { impact: MergeImpact }) {
  const decisionVerb = impact.decisionCount === 1 ? "needs" : "need";
  const autoMergeVerb = impact.autoMergeCount === 1 ? "merges" : "merge";

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        variant="outlined"
        color="info"
        label={`${impact.entryCount} ${pluralize("entry", impact.entryCount)} → 1`}
      />
      {impact.decisionCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label={`${impact.decisionCount} ${pluralize(
            "inventory overlap",
            impact.decisionCount
          )} ${decisionVerb} a decision`}
        />
      )}
      {impact.decisionCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={`${impact.filedCount} ${pluralize("report", impact.filedCount)} filed / ${
            impact.openCount
          } open ${pluralize("report", impact.openCount)}`}
        />
      )}
      {impact.autoMergeCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="success"
          label={`${impact.autoMergeCount} ${pluralize(
            "inventory",
            impact.autoMergeCount
          )} ${autoMergeVerb} automatically`}
        />
      )}
    </Stack>
  );
}

function OutcomeSummary({ impact, outcome }: { impact: MergeImpact; outcome: MergeOutcome }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        size="small"
        variant="outlined"
        color="info"
        label={`${impact.entryCount} ${pluralize("entry", impact.entryCount)} → 1`}
      />
      {outcome.autoMergeCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="success"
          label={`${outcome.autoMergeCount} merged automatically`}
        />
      )}
      {outcome.keptCount > 0 && (
        <Chip size="small" variant="outlined" label={`${outcome.keptCount} kept`} />
      )}
      {outcome.combinedCount > 0 && (
        <Chip size="small" variant="outlined" label={`${outcome.combinedCount} combined`} />
      )}
      {outcome.blankCount > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label={`${outcome.blankCount} left blank`}
        />
      )}
    </Stack>
  );
}

/**
 * The merge impact map: how many catalog entries collapse into one, how many
 * inventory overlaps need an explicit decision (split reports filed/open),
 * and how many merge automatically. In "outcome" mode (after a successful
 * merge) it shows
 * what actually happened instead of what's about to.
 */
export function MergeImpactSummary(props: MergeImpactSummaryProps) {
  if (props.mode === "outcome") {
    return (
      <Box data-testid="merge-impact-summary">
        {props.outcome && <OutcomeSummary impact={props.impact} outcome={props.outcome} />}
      </Box>
    );
  }
  return (
    <Alert severity="info" data-testid="merge-impact-summary">
      <PreviewSummary impact={props.impact} />
    </Alert>
  );
}
